import { Button } from 'antd'
import { Separator } from 'react-resizable-panels'
import styled from 'styled-components'

export const Root = styled.div`
  height: 100%;
  min-height: 0;
  color: #263957;
  background: #fff;
  font-family: var(--font-primary);
  .nora-panels { height: 100%; width: 100%; }
`

export const HistorySeparator = styled(Separator)<{ $mobile: boolean }>`
  display: ${({ $mobile }) => $mobile ? 'none' : 'flex'};
  flex: 0 0 6px;
  align-items: center;
  justify-content: center;
  width: 6px;
  background: #f7f9fc;
  cursor: col-resize;
  &:hover, &:focus-visible, &[data-separator='active'] { background: #e6eef9; }
  &:focus-visible { outline: 2px solid #0e5fc7; outline-offset: -2px; }
`

export const ResizeGrip = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-block: 8px;
  pointer-events: none;
  i { width: 2px; height: 2px; border-radius: 50%; background: #9aa7b8; }
`

export const CollapsedActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 18px 0;
  @media (max-width: 767px) { display: none; }
`

export const IconButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 36px;
    width: 36px;
    min-width: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: #52647f;
    background: transparent;
    box-shadow: none;
  }
  &&:hover:not(:disabled), &&[aria-expanded='true'] { color: #0e5fc7; background: #edf4fc; }
  &&:focus-visible { outline: 2px solid #0e5fc7; outline-offset: 2px; }
  &&:disabled { color: #9aa7b8; }
  &&.mobile-action { display: none; }
  @media (max-width: 767px) {
    && { flex-basis: 44px; width: 44px; min-width: 44px; height: 44px; }
    &&.mobile-action { display: inline-flex; }
  }
`

export const History = styled.nav<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #f7f9fc;
  border-right: 1px solid #dfe6f0;
  @media (max-width: 767px) {
    display: ${({ $collapsed }) => $collapsed ? 'none' : 'flex'};
    border-right: 0;
    border-bottom: 1px solid #dfe6f0;
  }
`

export const HistoryHeader = styled.header`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  min-height: 72px;
  box-sizing: border-box;
  padding: 12px 16px;
  border-bottom: 1px solid #edf1f6;
  h2 { margin: 0; color: #17345f; font-size: 13px; font-weight: 650; }
  @media (max-width: 767px) {
    min-height: 40px;
    padding: 12px 16px 0;
    border: 0;
    ${IconButton} { display: none; }
  }
`

export const ConversationList = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  padding: 12px 10px;
  > .ant-skeleton { padding: 8px; }
`

export const ConversationButton = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 7px;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid ${({ $selected }) => $selected ? '#d8e5f5' : 'transparent'};
  border-radius: 8px;
  background: ${({ $selected }) => $selected ? '#edf4fc' : 'transparent'};
  color: ${({ $selected }) => $selected ? '#0e4ea7' : '#435670'};
  text-align: left;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
  > span {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    overflow-wrap: anywhere;
    font-size: 12px;
    font-weight: ${({ $selected }) => $selected ? 650 : 500};
    line-height: 1.55;
  }
  > time { color: #708099; font-size: 11px; font-variant-numeric: tabular-nums; }
  &:hover:not(:disabled) { background: #edf3fa; border-color: #d8e5f5; }
  &:focus-visible { outline: 2px solid #0e5fc7; outline-offset: 1px; }
  &:disabled { cursor: default; opacity: 0.6; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`

export const HistoryNotice = styled.p`
  margin: 0;
  padding: 16px 20px;
  border-top: 1px solid #edf1f6;
  color: #708099;
  font-size: 11px;
  line-height: 1.6;
  @media (max-width: 767px) { display: none; }
`

export const ChatArea = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  min-height: 0;
  > [data-chat-body] { flex: 1; min-height: 0; }
  .ant-alert { margin: 12px 16px 0; flex-shrink: 0; border-radius: 8px; }
`

export const Markdown = styled.div`
  font-size: 13px;
  line-height: 1.65;
  overflow-wrap: anywhere;
  p { margin: 0 0 10px; }
  p:last-child { margin-bottom: 0; }
  ul, ol { padding-left: 20px; }
  a { color: var(--blue-darker); text-decoration: underline; }
`

export const Sources = styled.ul`
  margin: 12px 0 0;
  padding: 10px 0 0;
  border-top: 1px solid #edf1f6;
  list-style: none;
  color: #708099;
  font-size: 11px;
  line-height: 1.6;
  li + li { margin-top: 4px; }
`
